import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';

const BMRCalculator = () => {
  const [age, setAge] = useState(28);
  const [gender, setGender] = useState('male');
  const [height, setHeight] = useState(170);
  const [weight, setWeight] = useState(70);

  const { bmr, mifflin, harris } = useMemo(() => {
    // Mifflin-St Jeor (most accurate)
    const m = gender === 'male'
      ? 10 * weight + 6.25 * height - 5 * age + 5
      : 10 * weight + 6.25 * height - 5 * age - 161;
    // Harris-Benedict (revised 1984)
    const h = gender === 'male'
      ? 88.362 + 13.397 * weight + 4.799 * height - 5.677 * age
      : 447.593 + 9.247 * weight + 3.098 * height - 4.330 * age;
    return { bmr: m, mifflin: m, harris: h };
  }, [age, gender, height, weight]);

  const activities = [
    { label: 'Sedentary (little/no exercise)', factor: 1.2 },
    { label: 'Light (1–3 days/week)', factor: 1.375 },
    { label: 'Moderate (3–5 days/week)', factor: 1.55 },
    { label: 'Very Active (6–7 days)', factor: 1.725 },
    { label: 'Extra Active (athlete)', factor: 1.9 },
  ];

  return (
    <CalculatorLayout
      title="BMR Calculator"
      subtitle="Basal Metabolic Rate — calories your body burns at complete rest."
      category="Fitness"
      description={<>
        <p>Basal Metabolic Rate (BMR) is the number of calories your body needs to perform basic life-sustaining functions like breathing, circulation, and cell production while at rest.</p>
        <p>This calculator uses both the <strong>Mifflin-St Jeor</strong> equation (recommended, most accurate) and the older <strong>Harris-Benedict</strong> equation. Multiply BMR by an activity factor to get your daily calorie needs (TDEE).</p>
      </>}
      formulaNote="Mifflin-St Jeor: BMR = 10W + 6.25H − 5A + s   (s = +5 for men, −161 for women)"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Gender</label>
            <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="male" /> Male</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="female" /> Female</label>
            </RadioGroup>
          </div>
          <SliderInput label="Age" unit="yr" value={age} onChange={setAge} min={15} max={100} step={1} />
          <SliderInput label="Height" unit="cm" value={height} onChange={setHeight} min={120} max={220} step={1} />
          <SliderInput label="Weight" unit="kg" value={weight} onChange={setWeight} min={30} max={200} step={0.5} />
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your BMR</div>
            <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">{Math.round(bmr).toLocaleString()} <span className="text-base text-slate-500">cal/day</span></div>
            <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
              <Box label="Mifflin-St Jeor" value={`${Math.round(mifflin)} cal`} tone="saffron" />
              <Box label="Harris-Benedict" value={`${Math.round(harris)} cal`} tone="green" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-bharat-navy text-sm mb-3">Daily Calorie Needs (TDEE)</h3>
            <div className="space-y-2">
              {activities.map(a => (
                <div key={a.label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <div className="text-sm text-slate-700">{a.label}</div>
                    <div className="text-[10px] text-slate-400">× {a.factor}</div>
                  </div>
                  <div className="display-font font-bold text-saffron-700">{Math.round(bmr * a.factor)} cal</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-2.5 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c}`}>{value}</div>
    </div>
  );
};

export default BMRCalculator;
